import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasAccess, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";
import { DISCLAIMER_TEXT } from "@/lib/crisisResources";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PathFinderAvatar from "@/components/PathFinderAvatar";
import axios from "axios";
import { toast } from "sonner";
import {
  Send,
  Loader2,
  Trash2,
  ArrowLeft,
  Sparkles,
  History,
  Plus,
  Clock,
  MessageSquare,
  X,
  ChevronRight,
  CalendarDays,
  ChevronDown,
  ChevronUp
} from "lucide-react";

// PathFinder intro message for free users
const FREE_USER_INTRO = `Hi, I'm PathFinder — your AI reflection companion inside Compass.

I'm here to help you notice patterns and reflect deeper through simple techniques like asking questions and observing themes in what you share.

**Important:** I'm an AI companion, NOT a therapist or mental health professional. I don't diagnose, treat, or provide medical advice. For mental health support, please consult a qualified professional.

Try asking me anything — about your day, a challenge you're facing, or something you've been thinking about.`;

// Premium user intro message
const PREMIUM_INTRO = `Hi, I'm PathFinder — your AI reflection companion inside Compass.

I'm here to help you notice patterns, reflect deeper, and move forward with clarity through simple techniques like reflective questioning and gentle observation.

**Important:** I'm an AI companion for self-reflection and personal growth. I am NOT a therapist, counsellor, or mental health professional. I don't diagnose, treat, or provide medical advice. If you're experiencing mental health challenges, please reach out to a qualified professional.

What I do is simple: I ask questions to help you think, I observe patterns in what you share, and I offer alternative perspectives. Think of me as a thoughtful friend, not a specialist.

Where would you like to start? You could tell me about your day, share something on your mind, or ask me to look at patterns in your recent journal entries.`;

// PathFinder techniques explanation
const PATHFINDER_TECHNIQUES = `**How PathFinder Works:**
• **Reflective Questions** — I ask open-ended questions to help you explore your own thoughts
• **Pattern Observation** — I notice recurring themes in what you share
• **Alternative Perspectives** — I offer different ways to look at situations
• **Empathetic Listening** — I acknowledge your feelings without judgment

These are simple reflection techniques, not therapy or treatment.`;

export default function PathFinderPage() {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [freeTrialUsed, setFreeTrialUsed] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  // Session management
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showSessionsDialog, setShowSessionsDialog] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // Message balance state
  const [messageBalance, setMessageBalance] = useState({
    monthlyRemaining: 0,
    topupBalance: 0,
    monthlyAllowance: 0,
    totalAvailable: 0
  });
  const [isExhausted, setIsExhausted] = useState(false);
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupOptions, setTopupOptions] = useState([]);
  const [resetsOn, setResetsOn] = useState("");
  
  // Weekly reflection state
  const [weeklyReflection, setWeeklyReflection] = useState(null);
  const [isReflectionExpanded, setIsReflectionExpanded] = useState(false);
  const [isReflectionNew, setIsReflectionNew] = useState(false);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Check if user has premium access using the standard tier hierarchy
  // Tier hierarchy: free < monthly < quarterly < annual
  // PathFinder is a 'monthly' tier feature, so monthly+ users get full access
  const checkPremium = useCallback(() => {
    return hasAccess(subscription?.type, 'monthly', user);
  }, [subscription, user]);
  
  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);
  
  // Fetch message balance
  const fetchBalance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pathfinder/balance`, {
        withCredentials: true
      });
      
      setMessageBalance({
        monthlyRemaining: res.data.monthly_remaining || 0,
        topupBalance: res.data.topup_balance || 0,
        monthlyAllowance: res.data.monthly_allowance || 0,
        totalAvailable: res.data.total_available || 0
      });
      
      // Check if exhausted
      if (res.data.has_access === false || res.data.total_available <= 0) {
        setIsExhausted(true);
      } else {
        setIsExhausted(false);
      }
    } catch (error) {
      console.error("Failed to fetch balance:", error);
    }
  }, []);
  
  // Fetch top-up options
  const fetchTopupOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pathfinder/topup-options`, {
        withCredentials: true
      });
      setTopupOptions(res.data.options || []);
    } catch (error) {
      console.error("Failed to fetch topup options:", error);
    }
  }, []);
  
  // Fetch weekly reflection
  const fetchWeeklyReflection = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pathfinder/weekly-reflection`, {
        withCredentials: true
      });
      
      if (res.data.has_access && res.data.reflection) {
        setWeeklyReflection({
          content: res.data.reflection,
          weekId: res.data.week_id,
          generatedAt: res.data.generated_at,
          checkinCount: res.data.checkin_count,
          journalCount: res.data.journal_count
        });
        setIsReflectionNew(res.data.is_new);
        // Auto-expand if it's a new reflection
        if (res.data.is_new) {
          setIsReflectionExpanded(true);
        }
      }
    } catch (error) {
      // Fail silently - weekly reflection is not critical
      console.error("Failed to fetch weekly reflection:", error);
    }
  }, []);
  
  // Initialize PathFinder
  const initPathFinder = useCallback(async () => {
    const premium = checkPremium();
    setIsPremium(premium);
    
    if (premium) {
      // Premium user - fetch balance, weekly reflection, and load conversation history
      await fetchBalance();
      await fetchTopupOptions();
      await fetchWeeklyReflection();
      
      try {
        const historyRes = await axios.get(`${API}/pathfinder/conversation`, {
          withCredentials: true
        });
        
        // Store session_id from response
        if (historyRes.data.session_id) {
          setCurrentSessionId(historyRes.data.session_id);
        }
        
        if (historyRes.data.messages?.length > 0) {
          setMessages(historyRes.data.messages);
        } else {
          // First use - show intro
          setMessages([{
            role: "assistant",
            content: PREMIUM_INTRO,
            created_at: new Date().toISOString(),
            is_intro: true
          }]);
        }
      } catch (error) {
        console.error("PathFinder init error:", error);
        // Show intro on error
        setMessages([{
          role: "assistant",
          content: PREMIUM_INTRO,
          created_at: new Date().toISOString(),
          is_intro: true
        }]);
      }
    } else {
      // Free user - check if they've used their free trial
      try {
        const trialRes = await axios.get(`${API}/pathfinder/free-trial-status`, {
          withCredentials: true
        });
        
        if (trialRes.data.trial_used) {
          setFreeTrialUsed(true);
          setShowUpgradePrompt(true);
          // Show their previous trial conversation
          if (trialRes.data.messages?.length > 0) {
            setMessages(trialRes.data.messages);
          }
        } else {
          // Show intro for free user trial
          setMessages([{
            role: "assistant",
            content: FREE_USER_INTRO,
            created_at: new Date().toISOString(),
            is_intro: true
          }]);
        }
      } catch (error) {
        // Default to showing intro
        setMessages([{
          role: "assistant",
          content: FREE_USER_INTRO,
          created_at: new Date().toISOString(),
          is_intro: true
        }]);
      }
    }
    
    setIsFetchingHistory(false);
  }, [checkPremium, fetchBalance, fetchTopupOptions, fetchWeeklyReflection]);
  
  // Fetch previous sessions list
  const fetchSessions = useCallback(async () => {
    if (!isPremium) return;
    
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API}/pathfinder/sessions`, {
        withCredentials: true
      });
      setSessions(res.data.sessions || []);
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  }, [isPremium]);
  
  // Load a specific session
  const loadSession = async (sessionId) => {
    setIsFetchingHistory(true);
    try {
      const res = await axios.get(`${API}/pathfinder/sessions/${sessionId}`, {
        withCredentials: true
      });
      
      setMessages(res.data.messages || []);
      setCurrentSessionId(sessionId);
      setShowSessionsDialog(false);
    } catch (error) {
      toast.error("Couldn't load conversation");
    } finally {
      setIsFetchingHistory(false);
    }
  };
  
  // Start new conversation
  const startNewConversation = async () => {
    try {
      const res = await axios.post(`${API}/pathfinder/sessions/new`, {}, {
        withCredentials: true
      });
      
      setCurrentSessionId(res.data.session_id);
      setMessages([{
        role: "assistant",
        content: PREMIUM_INTRO,
        created_at: new Date().toISOString(),
        is_intro: true
      }]);
      setShowSessionsDialog(false);
      toast.success("New conversation started");
    } catch (error) {
      toast.error("Couldn't start new conversation");
    }
  };
  
  // Delete a session
  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    
    if (!window.confirm("Delete this conversation?")) return;
    
    try {
      await axios.delete(`${API}/pathfinder/sessions/${sessionId}`, {
        withCredentials: true
      });
      
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      
      // If deleting current session, start fresh
      if (sessionId === currentSessionId) {
        startNewConversation();
      }
      
      toast.success("Conversation deleted");
    } catch (error) {
      toast.error("Couldn't delete conversation");
    }
  };
  
  useEffect(() => {
    initPathFinder();
  }, [initPathFinder]);
  
  // Send message to PathFinder
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    // Check if free user has already used their trial
    if (!isPremium && freeTrialUsed) {
      toast.error("Upgrade to continue chatting with PathFinder");
      return;
    }
    
    // Check if premium user is exhausted
    if (isPremium && isExhausted) {
      setShowTopupDialog(true);
      return;
    }
    
    const userMessage = inputValue.trim();
    setInputValue("");
    
    // Add user message to UI immediately
    const userMsg = {
      role: "user",
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    
    setIsLoading(true);
    
    try {
      const endpoint = isPremium 
        ? `${API}/pathfinder/message${currentSessionId ? `?session_id=${currentSessionId}` : ''}`
        : `${API}/pathfinder/free-trial`;
      const response = await axios.post(
        endpoint,
        { message: userMessage },
        { withCredentials: true }
      );
      
      // Check if response indicates exhaustion
      if (response.data.error && response.data.reason === "allowance_exhausted") {
        setIsExhausted(true);
        setResetsOn(response.data.resets_on || "the 1st of next month");
        setShowTopupDialog(true);
        // Remove the user message since it wasn't processed
        setMessages(prev => prev.slice(0, -1));
        setInputValue(userMessage);
        setIsLoading(false);
        return;
      }
      
      // Check if response indicates global ceiling
      if (response.data.error && response.data.reason === "global_ceiling") {
        toast.info("PathFinder is temporarily unavailable. Please try again later.");
        setMessages(prev => prev.slice(0, -1));
        setInputValue(userMessage);
        setIsLoading(false);
        return;
      }
      
      // Update session_id if returned
      if (response.data.session_id && !currentSessionId) {
        setCurrentSessionId(response.data.session_id);
      }
      
      // Update balance from response
      if (response.data.monthly_remaining !== undefined) {
        setMessageBalance(prev => ({
          ...prev,
          monthlyRemaining: response.data.monthly_remaining,
          topupBalance: response.data.topup_balance,
          totalAvailable: response.data.monthly_remaining + response.data.topup_balance
        }));
        
        // Check if now exhausted
        if (response.data.monthly_remaining + response.data.topup_balance <= 0) {
          setIsExhausted(true);
        }
      }
      
      // Add PathFinder response
      const assistantMsg = {
        role: "assistant",
        content: response.data.response,
        created_at: response.data.timestamp || new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);
      
      // If free user, mark trial as used and show upgrade prompt
      if (!isPremium) {
        setFreeTrialUsed(true);
        setShowUpgradePrompt(true);
      }
      
    } catch (error) {
      console.error("PathFinder message error:", error);
      console.error("Error details:", error.response?.data);
      
      if (error.response?.status === 403 && !isPremium) {
        // Free trial already used
        setFreeTrialUsed(true);
        setShowUpgradePrompt(true);
        toast.error("Your free trial has been used. Upgrade to continue.");
      } else if (error.response?.status === 500) {
        toast.error("PathFinder is experiencing issues. Please try again later.");
      } else if (error.code === 'ERR_NETWORK') {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error("PathFinder couldn't respond. Please try again.");
      }
      
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Open sessions dialog
  const openSessionsDialog = () => {
    fetchSessions();
    setShowSessionsDialog(true);
  };
  
  // Format date for display
  const formatSessionDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };
  
  // Loading state
  if (isFetchingHistory) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        {/* Background with sunflower at 35% opacity */}
        <div 
          className="fixed inset-0 z-0"
          style={{
            backgroundImage: 'url(/images/sunflower-cosmic.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.35,
            pointerEvents: 'none'
          }}
        />
        <div className="fixed inset-0 z-0 bg-[#0b2821]/70" style={{ pointerEvents: 'none' }} />
        <div className="flex flex-col items-center gap-4 relative z-1">
          <PathFinderAvatar size={64} className="animate-pulse" />
          <p style={{ color: '#ffffff' }}>Loading PathFinder...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col pb-20 relative">
      {/* Background with sunflower at 35% opacity */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: 'url(/images/sunflower-cosmic.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.35,
          pointerEvents: 'none'
        }}
      />
      {/* Dark overlay for readability */}
      <div className="fixed inset-0 z-0 bg-[#0b2821]/70" style={{ pointerEvents: 'none' }} />
      
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#0b2821]/80 backdrop-blur-sm border-b border-white/20">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5" style={{ color: '#ffffff' }} />
            </button>
            <PathFinderAvatar size={36} />
            <div>
              <h1 className="text-lg font-heading" style={{ color: '#ffffff' }}>PathFinder</h1>
              <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>AI Reflection Companion</p>
            </div>
          </div>
          
          {isPremium && (
            <div className="flex items-center gap-1">
              <button
                onClick={openSessionsDialog}
                className="p-2 transition-colors"
                style={{ color: 'rgba(255, 255, 255, 0.8)' }}
                title="Previous conversations"
                data-testid="sessions-btn"
              >
                <History className="w-5 h-5" />
              </button>
              <button
                onClick={startNewConversation}
                className="p-2 transition-colors"
                style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                title="New conversation"
                data-testid="new-conversation-btn"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
        
        {/* Scope Disclaimer - Always Visible */}
        <div className="px-4 py-2 border-t border-white/5" style={{ backgroundColor: 'rgba(11, 40, 33, 0.8)' }}>
          <p className="text-xs text-center text-white/50">
            {DISCLAIMER_TEXT}
          </p>
        </div>
        
        {/* Message Balance Display - Only for premium users */}
        {isPremium && !isFetchingHistory && (
          <div className="px-4 py-2 bg-[#0b2821]/60 border-t border-white/10">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-4">
                <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Monthly: <span style={{ color: isExhausted && messageBalance.monthlyRemaining === 0 ? '#f87171' : '#4ade80' }}>
                    {messageBalance.monthlyRemaining}/{messageBalance.monthlyAllowance}
                  </span>
                </span>
                {messageBalance.topupBalance > 0 && (
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Top-up: <span style={{ color: '#60a5fa' }}>{messageBalance.topupBalance}</span>
                  </span>
                )}
              </div>
              {messageBalance.totalAvailable <= 10 && messageBalance.totalAvailable > 0 && (
                <button
                  onClick={() => setShowTopupDialog(true)}
                  className="text-xs px-2 py-1 rounded-full bg-[#BF9B5A]/20 hover:bg-[#BF9B5A]/30 transition-colors"
                  style={{ color: '#BF9B5A' }}
                >
                  Top up
                </button>
              )}
            </div>
          </div>
        )}
      </header>
      
      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-4 relative z-1">
        {/* Weekly Reflection Card */}
        {isPremium && weeklyReflection && (
          <div 
            data-testid="weekly-reflection-card"
            className="rounded-2xl border overflow-hidden transition-all duration-300"
            style={{ 
              backgroundColor: 'rgba(166, 147, 115, 0.1)', 
              borderColor: 'rgba(166, 147, 115, 0.3)'
            }}
          >
            {/* Header - Always visible */}
            <button
              onClick={() => setIsReflectionExpanded(!isReflectionExpanded)}
              data-testid="weekly-reflection-toggle"
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#BF9B5A]/10 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(166, 147, 115, 0.2)' }}>
                  <CalendarDays className="w-4 h-4" style={{ color: '#BF9B5A' }} />
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium" style={{ color: '#BF9B5A' }}>
                      Your Weekly Reflection
                    </span>
                    {isReflectionNew && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full" style={{ backgroundColor: '#BF9B5A', color: '#0b2821' }}>
                        NEW
                      </span>
                    )}
                  </div>
                  <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Based on {weeklyReflection.checkinCount} check-ins this week
                  </span>
                </div>
              </div>
              {isReflectionExpanded ? (
                <ChevronUp className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              ) : (
                <ChevronDown className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              )}
            </button>
            
            {/* Content - Collapsible */}
            {isReflectionExpanded && (
              <div className="px-4 pb-4">
                <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(11, 40, 33, 0.5)' }}>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: '#ffffff' }}>
                    {weeklyReflection.content}
                  </p>
                </div>
                <p className="mt-2 text-xs text-center" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                  Generated {new Date(weeklyReflection.generatedAt).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  {weeklyReflection.journalCount > 0 && ` • ${weeklyReflection.journalCount} journal entries`}
                </p>
              </div>
            )}
          </div>
        )}
        
        {messages.map((msg, index) => (
          <div
            key={msg.created_at || `msg-${index}`}
            className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
          >
            {msg.role === "assistant" && (
              <PathFinderAvatar size={32} className="shrink-0 mt-1" />
            )}
            
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.role === "user"
                  ? "ml-auto"
                  : ""
              }`}
              style={msg.role === "user" 
                ? { backgroundColor: '#BF9B5A', color: '#ffffff' }
                : { backgroundColor: 'rgba(11, 40, 33, 0.85)', color: '#ffffff' }
              }
            >
              <p className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: '#ffffff' }}>
                {msg.content}
              </p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex gap-3">
            <PathFinderAvatar size={32} className="shrink-0 mt-1" />
            <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: 'rgba(11, 40, 33, 0.85)' }}>
              <div className="flex items-center gap-2" style={{ color: '#ffffff' }}>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">PathFinder is reflecting...</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Upgrade Prompt for Free Users */}
        {showUpgradePrompt && !isPremium && (
          <div className="mt-6 rounded-2xl p-5 border" style={{ 
            backgroundColor: 'rgba(11, 40, 33, 0.85)', 
            borderColor: 'rgba(255, 255, 255, 0.3)' 
          }}>
            <div className="flex items-start gap-3 mb-4">
              <PathFinderAvatar size={40} className="shrink-0" />
              <p className="text-sm leading-relaxed" style={{ color: '#ffffff' }}>
                That's just a glimpse of what we can explore together. PathFinder remembers your journey, spots your patterns, and walks alongside you every step of the way.
              </p>
            </div>
            <p className="text-sm mb-4" style={{ color: '#ffffff' }}>
              Unlock full access to keep going.
            </p>
            <Button
              onClick={() => window.open(MEMBERSHIP_URL, "_blank")}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all hover:scale-[1.02]"
              style={{ 
                backgroundColor: '#BF9B5A', 
                color: '#ffffff',
                border: 'none'
              }}
            >
              <Sparkles className="w-4 h-4" />
              Unlock PathFinder
            </Button>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </main>
      
      {/* Input Area */}
      <div className="sticky bottom-16 bg-[#0b2821]/90 backdrop-blur-sm border-t px-4 py-3 relative z-1" style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
        {(!isPremium && freeTrialUsed) ? (
          <div className="text-center py-2">
            <p className="text-sm mb-2" style={{ color: '#ffffff' }}>
              Your free message has been used
            </p>
            <Button
              onClick={() => window.open(MEMBERSHIP_URL, "_blank")}
              className="flex items-center gap-2 mx-auto"
              style={{ 
                backgroundColor: '#BF9B5A', 
                color: '#ffffff',
                border: 'none'
              }}
            >
              <Sparkles className="w-4 h-4" />
              Unlock PathFinder
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 items-end">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Share what's on your mind..."
              className="flex-1 min-h-[44px] max-h-[120px] resize-none placeholder:text-white/60"
              style={{ 
                backgroundColor: 'rgba(11, 40, 33, 0.8)', 
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#ffffff'
              }}
              rows={1}
              disabled={isLoading}
            />
            
            <Button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="h-11 w-11 p-0 transition-all"
              style={{ 
                backgroundColor: '#BF9B5A', 
                color: '#ffffff',
                opacity: (!inputValue.trim() || isLoading) ? 0.5 : 1
              }}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        )}
      </div>
      
      {/* Previous Conversations Dialog */}
      <Dialog open={showSessionsDialog} onOpenChange={setShowSessionsDialog}>
        <DialogContent 
          className="sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col"
          style={{ 
            backgroundColor: '#0b2821', 
            borderColor: 'rgba(255, 255, 255, 0.3)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#ffffff' }}>
              <History className="w-5 h-5" />
              Previous Conversations
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-2 mt-4 pr-1">
            {/* New Conversation Button */}
            <button
              onClick={startNewConversation}
              className="w-full p-3 rounded-xl border flex items-center gap-3 transition-all hover:opacity-90"
              style={{ 
                backgroundColor: '#BF9B5A',
                borderColor: '#BF9B5A'
              }}
              data-testid="new-conversation-dialog-btn"
            >
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
              >
                <Plus className="w-5 h-5" style={{ color: '#ffffff' }} />
              </div>
              <span className="font-semibold" style={{ color: '#ffffff' }}>Start New Conversation</span>
            </button>
            
            {loadingSessions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#ffffff' }} />
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: '#ffffff' }} />
                <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  No previous conversations yet
                </p>
              </div>
            ) : (
              sessions.map((session) => (
                <button
                  key={session.session_id}
                  onClick={() => loadSession(session.session_id)}
                  className={`w-full p-3 rounded-xl border flex items-start gap-3 transition-all hover:bg-white/10 text-left ${
                    session.session_id === currentSessionId ? 'border-white' : ''
                  }`}
                  style={{ 
                    borderColor: session.session_id === currentSessionId 
                      ? '#ffffff' 
                      : 'rgba(255, 255, 255, 0.2)',
                    backgroundColor: session.session_id === currentSessionId 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'transparent'
                  }}
                  data-testid={`session-${session.session_id}`}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                  >
                    <MessageSquare className="w-4 h-4" style={{ color: '#ffffff' }} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p 
                      className="text-sm font-medium truncate mb-1"
                      style={{ color: '#ffffff' }}
                    >
                      {session.preview || "Conversation"}
                    </p>
                    <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      <Clock className="w-3 h-3" />
                      <span>{formatSessionDate(session.last_activity)}</span>
                      <span>•</span>
                      <span>{session.message_count} messages</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={(e) => deleteSession(session.session_id, e)}
                    className="p-1.5 rounded-lg transition-colors hover:bg-red-500/20"
                    style={{ color: 'rgba(255, 255, 255, 0.4)' }}
                    title="Delete conversation"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      <BottomNavigation />
      
      {/* Top-up Dialog */}
      <Dialog open={showTopupDialog} onOpenChange={setShowTopupDialog}>
        <DialogContent className="bg-[#0b2821] border border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#BF9B5A]" />
              {isExhausted ? "Messages Used Up" : "Top Up Messages"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {isExhausted && (
              <div className="p-3 rounded-lg bg-amber-900/20 border border-amber-500/20">
                <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Your monthly PathFinder messages are used up. Your allowance resets on <strong>{resetsOn || "the 1st of next month"}</strong>.
                </p>
              </div>
            )}
            
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Need more messages now? Top up your balance:
            </p>
            
            <div className="grid gap-3">
              {topupOptions.map((option) => (
                <button
                  key={option.messages}
                  onClick={() => {
                    // Initiate top-up payment
                    axios.post(`${API}/pathfinder/topup/initiate`, 
                      { messages: option.messages },
                      { withCredentials: true }
                    ).then(res => {
                      if (res.data.payment_url) {
                        window.location.href = res.data.payment_url;
                      }
                    }).catch(err => {
                      toast.error("Could not initiate payment. Please try again.");
                    });
                  }}
                  className="p-4 rounded-xl border border-white/20 hover:border-[#BF9B5A]/50 hover:bg-[#BF9B5A]/10 transition-all text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">{option.messages} Messages</p>
                      <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Never expire</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#BF9B5A]">{option.price_display}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                <strong>Note:</strong> Top-up messages never expire and carry over indefinitely. If you cancel your subscription, your top-up balance is retained and will be available if you resubscribe.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
